// frontend/context/SocketContext.tsx
import React, { createContext, useContext, useEffect, useState } from "react";
import type Pusher from "pusher-js";
import { env } from "@/config/env";
import { logger } from "@/lib/logger";

interface SocketContextProps {
    pusher: Pusher | null;
}

const SocketContext = createContext<SocketContextProps>({ pusher: null });

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [pusher, setPusher] = useState<Pusher | null>(null);

    useEffect(() => {
        if (!env.PUSHER_KEY) return;

        let instance: Pusher | null = null;
        let cancelled = false;

        const initializePusher = async () => {
            try {
                const PusherClient = (await import("pusher-js")).default;
                if (cancelled) return;

                instance = new PusherClient(env.PUSHER_KEY, {
                    cluster: env.PUSHER_CLUSTER,
                    authEndpoint: `${env.API_BASE}/pusher/auth`,
                    forceTLS: true,
                });
                setPusher(instance);
                logger.debug("socket.initialized", { cluster: env.PUSHER_CLUSTER });
            } catch (error) {
                logger.error("socket.initialization_failed", error);
            }
        };

        void initializePusher();

        return () => {
            cancelled = true;
            instance?.disconnect();
        };
    }, []);

    return (
        <SocketContext.Provider value={{ pusher }}>
            {children}
        </SocketContext.Provider>
    );
};

export const useSocket = () => useContext(SocketContext);
